"use client"

import * as React from "react"
import { useNavigate } from "react-router"

import { PrimaryPageTemplate } from "@/components/templates/primary-page-template"
import { useProduct } from "@/contexts/product-context"
import { useActiveScope } from "@/hooks/use-active-scope"
import { useStepsEmbedNavMode } from "@/hooks/use-steps-embed-nav-mode"
import { buildProgramEmbedScope } from "@/lib/program-embed-scope"
import {
  isPrismDashboardNavMessage,
  resolveStepsDashboardNavHref,
} from "@/lib/prism-dashboard-embed-nav"
import {
  postPrismEmbedScope,
  type PrismEmbedScope,
} from "@/lib/prism-embed-scope"
import {
  COMPLIANCE_DEMO_SCREEN,
  prismDemoScreenUrl,
  type PrismDemoScreenId,
} from "@/lib/prism-demo-screens"
import { productSlug } from "@/stores/app-store"
import { cn } from "@/lib/utils"

export type PrismDemoScreenFrameProps = {
  screen?: PrismDemoScreenId
  title?: string
  className?: string
}

function embedScopeForProgramScreen(
  screen: PrismDemoScreenId,
  parent: ReturnType<typeof useActiveScope>["parent"],
  child: ReturnType<typeof useActiveScope>["child"],
  status: ReturnType<typeof useActiveScope>["status"],
): PrismEmbedScope | undefined {
  if (screen !== "program-program-details" || status !== "open" || !parent) return undefined
  return buildProgramEmbedScope(parent, child)
}

/**
 * Full-height STEPS demo iframe for Prism suite products.
 * Keeps Exxat shell chrome; legacy UI renders in the main pane only.
 */
export function PrismDemoScreenFrame({
  screen = COMPLIANCE_DEMO_SCREEN,
  title = "Dashboard",
  className,
}: PrismDemoScreenFrameProps) {
  const navigate = useNavigate()
  const { product } = useProduct()
  const productRoot = productSlug(product)
  const { parent, child, status } = useActiveScope(product)
  const [embedNav] = useStepsEmbedNavMode()
  const embedScope = embedScopeForProgramScreen(screen, parent, child, status)
  // These hubs use the demo's top tabs. The host sidebar stays hidden.
  const frameNav =
    screen === "curriculum-course-offerings" ||
    screen === "profile-search" ||
    screen === "student-dashboard" ||
    screen === "caas-faculty-by-group" ||
    screen === "admin-faculty-search" ||
    screen === "admin-tenant-apps" ||
    screen === "one-school-dashboard"
      ? "tabs"
      : embedNav
  const omitSiteHeader = screen === "admin-tenant-apps"
  const src = prismDemoScreenUrl(screen, { embed: true, scope: embedScope, embedNav: frameNav })
  const iframeRef = React.useRef<HTMLIFrameElement>(null)

  const pushScope = React.useCallback(() => {
    if (!embedScope) return
    postPrismEmbedScope(iframeRef.current?.contentWindow, embedScope)
  }, [embedScope])

  const onIframeLoad = React.useCallback(() => {
    pushScope()
    if (screen !== "program-program-details" || embedNav !== "sidebar") return
    iframeRef.current?.contentWindow?.postMessage({ type: "PRISM_EMBED_NAV_APPLY" }, "*")
  }, [embedNav, pushScope, screen])

  React.useEffect(() => {
    pushScope()
  }, [pushScope, src])

  React.useEffect(() => {
    if (screen !== "program-program-details" || embedNav !== "sidebar") return
    iframeRef.current?.contentWindow?.postMessage({ type: "PRISM_EMBED_NAV_APPLY" }, "*")
  }, [embedNav, screen, src])

  React.useEffect(() => {
    if (screen !== "clinical-dashboard" && screen !== "faculty-dashboard") return

    function onMessage(event: MessageEvent) {
      if (!isPrismDashboardNavMessage(event.data)) return
      const href = resolveStepsDashboardNavHref(event.data.linkId, productRoot)
      if (!href) return
      navigate(href)
    }

    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [navigate, productRoot, screen])

  return (
    <PrimaryPageTemplate
      siteHeader={omitSiteHeader ? undefined : { title }}
      maxWidthClassName="max-w-none"
      contentClassName="flex min-h-0 flex-1 flex-col p-0"
      bodyClassName="flex min-h-0 flex-1 flex-col"
      contentFillViewport={false}
    >
      <iframe
        ref={iframeRef}
        title={`${title} demo`}
        src={src}
        onLoad={onIframeLoad}
        className={cn(
          "block w-full flex-1 border-0 bg-background",
          omitSiteHeader ? "min-h-[calc(100svh-3.5rem)]" : "min-h-[calc(100svh-7rem)]",
          className,
        )}
        loading="eager"
      />
    </PrimaryPageTemplate>
  )
}
